import { z } from "zod";
import { FORMAS_PAGAMENTO } from "../dominio";
import { centavos, email, id, pin } from "./comuns";

const observacoes = z.string().trim().max(500).optional();

export const caixaAbrirSchema = z.object({
  eventoId: id,
  fundoTrocoCentavos: centavos,
  observacoes,
});
export type CaixaAbrirEntrada = z.input<typeof caixaAbrirSchema>;

export const caixaFecharSchema = z.object({
  contado: z.object({ DINHEIRO: centavos, PIX: centavos, DEBITO: centavos, CREDITO: centavos }),
  observacoes,
});
export type CaixaFecharEntrada = z.input<typeof caixaFecharSchema>;

const motivo = z.string().trim().min(3, { error: "Informe o motivo" }).max(500);

export const caixaReabrirSchema = z.object({ motivo });
export type CaixaReabrirEntrada = z.input<typeof caixaReabrirSchema>;

/**
 * Autorização de um ADMIN digitada na hora, no aparelho do operador: o PIN do ADMIN ou o
 * e-mail e a senha dele. Quando quem está logado já é ADMIN, a própria sessão autoriza.
 */
export type AutorizacaoAdmin = { tipo: "PIN"; pin: string } | { tipo: "SENHA"; adminEmail: string; adminSenha: string };

const ehObjeto = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

/** Aparelhos com a versão anterior em cache mandam só e-mail e senha, sem o "tipo". */
const comTipo = (v: unknown) => (ehObjeto(v) && !("tipo" in v) && "adminEmail" in v ? { ...v, tipo: "SENHA" } : v);

export const autorizacaoAdminSchema = z.preprocess(
  comTipo,
  z.discriminatedUnion("tipo", [
    z.object({ tipo: z.literal("PIN"), pin }),
    z.object({
      tipo: z.literal("SENHA"),
      adminEmail: email,
      adminSenha: z.string().min(1, { error: "Informe a senha do ADMIN" }).max(200),
    }),
  ]),
);

export const vendaItemSchema = z.object({
  produtoId: id,
  quantidade: z.number().int().min(1).max(999),
  /** Preço mostrado no aparelho; o servidor confere com o preço atual. */
  precoUnitCentavos: centavos,
});

export const vendaRegistrarSchema = z
  .object({
    /** UUID gerado no aparelho: reenviar o mesmo id nunca duplica a venda. */
    id,
    caixaId: id,
    formaPagamento: z.enum(FORMAS_PAGAMENTO),
    itens: z.array(vendaItemSchema).min(1, { error: "A venda precisa ter pelo menos um item" }).max(100),
    recebidoCentavos: centavos.nullable().optional(),
    /** Hora da venda no aparelho (vale para vendas que ficaram na fila offline). */
    criadaEm: z.iso.datetime({ offset: true }),
    viaFilaOffline: z.boolean().optional().default(false),
  })
  .refine((v) => new Set(v.itens.map((i) => i.produtoId)).size === v.itens.length, {
    error: "Produto repetido na venda",
    path: ["itens"],
  })
  .refine((v) => v.recebidoCentavos == null || v.formaPagamento === "DINHEIRO", {
    error: "Valor recebido só se aplica a pagamento em dinheiro",
    path: ["recebidoCentavos"],
  });
export type VendaRegistrarEntrada = z.input<typeof vendaRegistrarSchema>;

export const vendaLoteSchema = z.object({
  // Cada venda é validada separadamente no servidor: uma inválida não derruba o lote.
  vendas: z.array(z.unknown()).min(1).max(50),
});

export const vendaComAutorizacaoSchema = z.object({
  venda: vendaRegistrarSchema,
  /** Opcional só quando quem está logado é ADMIN. */
  autorizacao: autorizacaoAdminSchema.optional(),
});
export type VendaComAutorizacaoEntrada = { venda: VendaRegistrarEntrada; autorizacao?: AutorizacaoAdmin };

/** Formato antigo do cancelamento: e-mail e senha soltos no corpo, junto do motivo. */
const cancelamentoLegado = (v: unknown) =>
  ehObjeto(v) && "adminEmail" in v && !("autorizacao" in v)
    ? { motivo: v.motivo, autorizacao: { tipo: "SENHA", adminEmail: v.adminEmail, adminSenha: v.adminSenha } }
    : v;

export const vendaCancelarSchema = z.preprocess(
  cancelamentoLegado,
  z.object({
    motivo: z.string().trim().min(3, { error: "Informe o motivo do cancelamento" }).max(300),
    autorizacao: autorizacaoAdminSchema.optional(),
  }),
);
export type VendaCancelarEntrada = { motivo: string; autorizacao?: AutorizacaoAdmin };

/**
 * Ajuste de uma venda válida: trocar a forma de pagamento e/ou diminuir quantidades (0 tira
 * o item). Não acrescenta itens nem muda preço; para isso, registra-se uma venda nova.
 */
export const vendaAjustarSchema = z
  .object({
    motivo: z.string().trim().min(3, { error: "Informe o motivo do ajuste" }).max(300),
    formaPagamento: z.enum(FORMAS_PAGAMENTO).optional(),
    /** Só no dinheiro: quanto o cliente entregou (para recalcular o troco). */
    recebidoCentavos: centavos.nullable().optional(),
    /** Nova quantidade de cada item que muda; os itens que não aparecem ficam como estão. */
    itens: z
      .array(z.object({ produtoId: id, quantidade: z.number().int().min(0).max(999) }))
      .max(100)
      .optional(),
    autorizacao: autorizacaoAdminSchema.optional(),
  })
  .refine((v) => !v.itens || new Set(v.itens.map((i) => i.produtoId)).size === v.itens.length, {
    error: "Produto repetido no ajuste",
    path: ["itens"],
  });
export type VendaAjustarEntrada = {
  motivo: string;
  formaPagamento?: (typeof FORMAS_PAGAMENTO)[number];
  recebidoCentavos?: number | null;
  itens?: { produtoId: string; quantidade: number }[];
  autorizacao?: AutorizacaoAdmin;
};

/**
 * Motivos pelos quais o servidor recusa uma venda que já foi entregue no balcão. Os três
 * primeiros podem ser aceitos com autorização de ADMIN quando a venda veio da fila offline.
 */
export const CONFLITOS_AUTORIZAVEIS = ["ESTOQUE_INSUFICIENTE", "PRECO_DIVERGENTE", "FORA_DO_CARDAPIO"] as const;
export type ConflitoAutorizavel = (typeof CONFLITOS_AUTORIZAVEIS)[number];
